import { Component, ElementRef, OnInit } from '@angular/core';
import { ApiService } from '../../core/http/api.service';
import { UtilsService } from '../../core/services/utils/utils.service';

interface TwitterGoldenListForm {
  id: string;
  name: string;
  xHandle: string;
  xUrl: string;
  primaryDomain: string;
  notableBooksWork: string;
  isActive: boolean;
}

@Component({
  selector: 'app-twitter-golden-list',
  templateUrl: './twitter-golden-list.component.html',
  styleUrls: ['./twitter-golden-list.component.css'],
  standalone: false,
})
export class TwitterGoldenListComponent implements OnInit {
  readonly profilePlaceholder = 'assets/images/user_avatar.png';
  loading = false;
  saving = false;
  importing = false;
  showImportModal = false;
  importFile: File | null = null;
  importReport: any = null;
  readonly importSampleUrl =
    'https://bhakts.s3.us-east-2.amazonaws.com/csv/twitter-golden-handles-sample.csv';
  showModal = false;
  showViewModal = false;
  selectedMember: any = null;
  memberPosts: any[] = [];
  memberPostsLoading = false;
  expandedPostText = new Set<string>();
  truncatedPostText = new Set<string>();
  isEditMode = false;
  members: any[] = [];
  page = 1;
  perPage = 20;
  total = 0;
  filtersApplied = false;
  filters = { q: '', isActive: 'true' };
  form: TwitterGoldenListForm = this.emptyForm();

  constructor(
    private api: ApiService,
    private utils: UtilsService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.loadMembers();
  }

  emptyForm(): TwitterGoldenListForm {
    return {
      id: '',
      name: '',
      xHandle: '',
      xUrl: '',
      primaryDomain: '',
      notableBooksWork: '',
      isActive: true,
    };
  }

  loadMembers(): void {
    this.loading = true;
    const query: any = { page: this.page, limit: this.perPage };
    if (this.filters.q.trim()) query.q = this.filters.q.trim();
    if (this.filters.isActive !== 'all')
      query.is_active = this.filters.isActive;

    this.api.getTwitterGoldenList(query).subscribe(
      (res) => {
        this.members = Array.isArray(res?.data) ? res.data : [];
        this.total = Number(res?.meta?.total || 0);
        this.loading = false;
      },
      (err) => {
        this.loading = false;
        this.utils.resErr(err);
      },
    );
  }

  applyFilters(): void {
    this.filtersApplied = true;
    this.page = 1;
    this.loadMembers();
  }

  resetFilters(): void {
    this.filters = { q: '', isActive: 'true' };
    this.filtersApplied = false;
    this.page = 1;
    this.loadMembers();
  }

  pageChange(page: number): void {
    this.page = page;
    this.loadMembers();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.form = this.emptyForm();
    this.showModal = true;
  }

  openEditModal(member: any): void {
    this.isEditMode = true;
    this.form = {
      id: member.id,
      name: member.name || '',
      xHandle: member.xHandle || '',
      xUrl: member.xUrl || '',
      primaryDomain: member.primaryDomain || '',
      notableBooksWork: member.notableBooksWork || '',
      isActive: member.isActive !== false,
    };
    this.showModal = true;
  }

  openViewModal(member: any): void {
    this.selectedMember = member;
    this.showViewModal = true;
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.selectedMember = null;
    this.memberPosts = [];
    this.expandedPostText.clear();
    this.truncatedPostText.clear();
  }

  isPostTextTruncated(key: string): boolean {
    return this.truncatedPostText.has(key);
  }

  togglePostText(key: string): void {
    if (this.expandedPostText.has(key)) this.expandedPostText.delete(key);
    else this.expandedPostText.add(key);
  }

  private measureTruncatedPostText(): void {
    this.truncatedPostText.clear();
    const textElements = this.elementRef.nativeElement.querySelectorAll<HTMLElement>(
      '[data-post-text-key]',
    );
    textElements.forEach((element) => {
      if (element.scrollHeight > element.clientHeight + 1) {
        const key = element.dataset['postTextKey'];
        if (key) this.truncatedPostText.add(key);
      }
    });
  }

  metadataEntries(metadata: any): Array<{ key: string; value: any }> {
    if (!metadata || typeof metadata !== 'object') return [];
    return Object.keys(metadata).map((key) => ({ key, value: metadata[key] }));
  }

  metadataLabel(key: string): string {
    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  metadataValue(value: any): string {
    if (value === null || value === undefined || value === '')
      return 'Not available';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  compactNumber(value: any): string {
    const number = Number(value || 0);
    return Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(number);
  }

  profileWebsite(metadata: any): string {
    return (
      metadata?.url || metadata?.entities?.url?.urls?.[0]?.expanded_url || ''
    );
  }

  highResolutionProfilePicture(url: string): string {
    return String(url || '').replace(
      /_normal(?=\.[a-z0-9]+(?:\?|$))/i,
      '_400x400',
    );
  }

  closeModal(): void {
    if (!this.saving) this.showModal = false;
  }

  onHandleChange(value: string): void {
    this.form.xHandle = value;
    if (!this.isEditMode || !this.form.xUrl) {
      const handle = String(value || '')
        .trim()
        .replace(/^@/, '');
      this.form.xUrl = handle ? `https://x.com/${handle}` : '';
    }
  }

  isFormValid(): boolean {
    return Boolean(
      this.form.name.trim() &&
      this.form.xHandle.trim() &&
      this.form.xUrl.trim(),
    );
  }

  save(): void {
    if (!this.isFormValid() || this.saving) return;
    this.saving = true;
    const payload = {
      name: this.form.name.trim(),
      xHandle: this.form.xHandle.trim(),
      xUrl: this.form.xUrl.trim(),
      primaryDomain: this.form.primaryDomain.trim(),
      notableBooksWork: this.form.notableBooksWork.trim(),
      isActive: this.form.isActive,
    };
    const request = this.isEditMode
      ? this.api.updateTwitterGoldenListMember(this.form.id, payload)
      : this.api.createTwitterGoldenListMember(payload);

    request.subscribe(
      () => {
        this.saving = false;
        this.showModal = false;
        this.utils.toastSuccess(
          `Member ${this.isEditMode ? 'updated' : 'added'} successfully`,
        );
        this.loadMembers();
      },
      (err) => {
        this.saving = false;
        this.utils.resErr(err);
      },
    );
  }

  openImportModal(): void {
    this.importFile = null;
    this.importReport = null;
    this.showImportModal = true;
  }

  closeImportModal(): void {
    if (!this.importing) this.showImportModal = false;
  }

  selectImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.utils.toastError('Please select a CSV file');
      input.value = '';
      return;
    }
    this.importFile = file;
    this.importReport = null;
  }

  viewImportFile(): void {
    if (!this.importFile) return;
    const url = URL.createObjectURL(this.importFile);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  importHandles(): void {
    if (!this.importFile || this.importing) return;

    this.importing = true;
    this.api.importTwitterGoldenList(this.importFile).subscribe(
      (res) => {
        this.importing = false;
        this.importReport = res?.data || {};
        this.utils.toastSuccess('CSV import completed');
        this.page = 1;
        this.loadMembers();
      },
      (err) => {
        this.importing = false;
        this.utils.resErr(err);
      },
    );
  }

  deactivate(member: any): void {
    if (!confirm(`Deactivate ${member.name}?`)) return;
    this.api
      .updateTwitterGoldenListMember(member.id, { isActive: false })
      .subscribe(
        () => {
          this.utils.toastSuccess('Member deactivated successfully');
          this.loadMembers();
        },
        (err) => this.utils.resErr(err),
      );
  }

  deleteMember(member: any): void {
    if (!confirm(`Permanently delete ${member.name}?`)) return;
    this.api.deleteTwitterGoldenListMember(member.id).subscribe(
      () => {
        this.utils.toastSuccess('Member deleted successfully');
        this.loadMembers();
      },
      (err) => this.utils.resErr(err),
    );
  }

  handleCopyToClipboard(value: string): void {
    this.utils.copyToClipboard(value);
    this.utils.toastSuccess('ID copied to your clipboard successfully');
  }

  onProfileImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (!image.src.endsWith(this.profilePlaceholder)) {
      image.src = this.profilePlaceholder;
    }
  }

  reactivate(member: any): void {
    this.api
      .updateTwitterGoldenListMember(member.id, { isActive: true })
      .subscribe(
        () => {
          this.utils.toastSuccess('Member activated successfully');
          this.loadMembers();
        },
        (err) => this.utils.resErr(err),
      );
  }
}
